import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../App.css';

import Loader from '../components/Loader';
import BrandLogo from '../components/BrandLogo';
import { ArrowLeftIcon, AlertIcon, ChatIcon, MailIcon } from '../components/Icons';
import { BASE_URL } from '../App';
import { trackProductViewed, useTrackError } from '../utils/analytics';
import { readSelectedItem, writeSelectedItem } from '../utils/portalConfig';
import { formatMoney, getDiscountPercent, getItemId } from '../utils/format';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/** Only reuse the cached item when it is the bundle this URL asks for. */
const cachedBundleFor = (bundleId) => {
  const cached = readSelectedItem();
  if (!cached || !cached.is_bundle) return null;
  return String(getItemId(cached)) === String(bundleId) ? cached : null;
};

const BundleDetails = () => {
  const { bundleId } = useParams();
  const navigate = useNavigate();

  const [selectedBundle, setSelectedBundle] = useState(() => cachedBundleFor(bundleId));
  const [isLoadingBundle, setIsLoadingBundle] = useState(false);
  const [bundleLoadError, setBundleLoadError] = useState(null);
  useTrackError('bundle_load', bundleLoadError);

  useEffect(() => {
    setSelectedBundle(cachedBundleFor(bundleId));
  }, [bundleId]);

  useEffect(() => {
    let cancelled = false;

    const loadBundle = async () => {
      if (selectedBundle || !bundleId) return;

      const portalToken = localStorage.getItem('portalToken');

      if (!portalToken) {
        sessionStorage.setItem('returnTo', `/bundle-details/${bundleId}`);
        const partnerSlug = localStorage.getItem('partnerSlug');
        navigate(partnerSlug ? `/${partnerSlug}` : '/');
        return;
      }

      setIsLoadingBundle(true);
      setBundleLoadError(null);

      try {
        const response = await fetch(`${BASE_URL}/partner/v2/get-portal-plans`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${portalToken}` },
          redirect: 'follow',
        });

        if (response.status === 401) {
          localStorage.removeItem('portalToken');
          sessionStorage.setItem('returnTo', `/bundle-details/${bundleId}`);
          const partnerSlug = localStorage.getItem('partnerSlug');
          navigate(partnerSlug ? `/${partnerSlug}` : '/');
          return;
        }

        const result = await response.json();
        if (cancelled) return;

        if (!Array.isArray(result.plans)) {
          setBundleLoadError('Unable to load bundle details.');
          return;
        }

        const bundle = result.plans.find(
          (p) => p.is_bundle && String(getItemId(p)) === String(bundleId)
        );

        if (!bundle) {
          setBundleLoadError(
            'This bundle is no longer available in your marketplace.'
          );
          return;
        }

        setSelectedBundle(bundle);
        writeSelectedItem(bundle);
      } catch (err) {
        console.error('Error loading bundle:', err);
        if (!cancelled) {
          setBundleLoadError('Failed to load bundle details. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoadingBundle(false);
      }
    };

    loadBundle();
    return () => {
      cancelled = true;
    };
  }, [bundleId, selectedBundle, navigate]);

  // Keyed on the id: the cached bundle is re-read (a new object) on mount,
  // which used to report the same view twice.
  const viewedBundleId = selectedBundle ? String(getItemId(selectedBundle)) : null;
  useEffect(() => {
    if (viewedBundleId) trackProductViewed(selectedBundle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedBundleId]);

  const discount = useMemo(
    () => getDiscountPercent(selectedBundle?.bundle_mrp, selectedBundle?.bundle_cost),
    [selectedBundle]
  );

  if (isLoadingBundle) {
    return (
      <div className="page">
        <div className="statusWrap">
          <Loader />
          <p className="statusBody">Loading bundle details…</p>
        </div>
      </div>
    );
  }

  if (bundleLoadError || !selectedBundle) {
    return (
      <div className="page">
        <div className="statusWrap">
          <div className="statusCard">
            <div className="statusIcon statusIconBad" aria-hidden="true">
              <AlertIcon width="28" height="28" />
            </div>
            <h1 className="statusTitle">Bundle unavailable</h1>
            <p className="statusBody">
              {bundleLoadError || 'Pick a bundle from the marketplace to continue.'}
            </p>
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={() => navigate('/marketplace')}
              >
                Back to marketplace
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currency = selectedBundle.currency;
  const included = selectedBundle.plan_list || [];

  return (
    <div className="page">
      <div className="detailShell">
        <button type="button" className="backLink" onClick={() => navigate('/marketplace')}>
          <ArrowLeftIcon />
          Back to marketplace
        </button>

        <div className="detailGrid">
          <div>
            <div className="card">
              <div className="detailHero">
                <BrandLogo
                  className="detailLogo"
                  src={selectedBundle.product_logo_url}
                  name={selectedBundle.product_name}
                />
                <div style={{ minWidth: 0 }}>
                  <h1 className="detailBrand">{selectedBundle.product_name}</h1>
                  <div className="detailPlanName">
                    {included.length} subscriptions in one bundle
                  </div>
                </div>
                {discount ? (
                  <span className="dealBadge" style={{ marginLeft: 'auto' }}>
                    {discount}% OFF
                  </span>
                ) : null}
              </div>

              {selectedBundle.plan_description && (
                <p className="detailDesc">{selectedBundle.plan_description}</p>
              )}
            </div>

            {included.length > 0 && (
              <div className="card">
                <div className="cardTitle">What's included</div>
                <div className="bundleIncluded">
                  {included.map((plan, i) => (
                    <div className="bundleItem" key={`${plan.vendor_name}-${i}`}>
                      <BrandLogo
                        className="offerLogo"
                        src={plan.vendor_logo_url}
                        name={plan.vendor_name}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div className="bundleItemName">{plan.vendor_name}</div>
                        <div className="bundleItemMeta">
                          {[plan.plan_name, plan.subscription_duration]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="detailAside">
            <div className="card">
              <div className="cardTitle">Price details</div>

              {discount ? (
                <div className="priceRow">
                  <span>Bought separately</span>
                  <span className="priceValue priceStrike">
                    {formatMoney(selectedBundle.bundle_mrp, currency)}
                  </span>
                </div>
              ) : null}

              <div className="priceRow priceRowTotal">
                <span>Bundle price</span>
                <span className="priceTotal">
                  {formatMoney(selectedBundle.bundle_cost, currency)}
                </span>
              </div>
            </div>

            {/* The portal checkout (create-portal-order) only resolves single
                plans — there is no bundle path behind it, which is why the old
                "Purchase Bundle" button had no click handler at all. Rather
                than a button that silently does nothing, route the member to a
                human who can complete it. */}
            <div className="card">
              <div className="cardTitle">Buying this bundle</div>
              <p className="howBody" style={{ marginTop: 0 }}>
                Bundle checkout isn't self-serve yet. Message us and we'll set it
                up for you, usually within a few minutes.
              </p>
              <div className="supportRow">
                <a
                  className={cx('btn', 'btnMd', 'btnBrand', 'btnBlock')}
                  href={`https://wa.me/919148477518?text=${encodeURIComponent(
                    `Hi, I'd like to buy the ${selectedBundle.product_name} bundle (${formatMoney(
                      selectedBundle.bundle_cost,
                      currency
                    )}).`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ChatIcon />
                  Request this bundle
                </a>
                <a
                  className={cx('btn', 'btnSm', 'btnSecondary', 'btnBlock')}
                  href={`mailto:help@getfleek.app?subject=${encodeURIComponent(
                    `Bundle request: ${selectedBundle.product_name}`
                  )}`}
                >
                  <MailIcon />
                  Email instead
                </a>
              </div>
            </div>

            <div className="card">
              <div className="cardTitle">Prefer a single subscription?</div>
              <p className="howBody" style={{ marginTop: 0 }}>
                Every individual plan in your marketplace can be bought instantly.
              </p>
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnSecondary', 'btnBlock')}
                style={{ marginTop: 14 }}
                onClick={() => navigate('/marketplace#plans')}
              >
                Browse single plans
              </button>
            </div>
          </aside>
        </div>

        <div className="pageFoot" />
      </div>
    </div>
  );
};

export default BundleDetails;
